import React, {useState} from 'react';

import {useCurrentUserState} from 'app/current-user/api';
import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {GeneralCreateItem} from 'app/general/widgets/GeneralCreateItem';
import {useAccountSettingsState} from 'app/settings/api';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {useCreateUser, useInviteUser} from 'app/user/api';
import {refreshPopoversPositions} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';

import {GuestUserToggleField} from './GuestUserToggleField';

import type {NewInvitationType} from 'app/user/types';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

export interface Props extends FormErrorsProps {
  showTeams: boolean;
  onCreateUser: (user: UserSerializedType) => Promise<unknown>;
  onConfirm?: () => Promise<unknown>;
  onCancel?: () => void;
  isPopover: boolean;
  className?: string;
  showCancelButton?: boolean;
  hideGuestInvite?: boolean;
}

export const CreateUserForm = createFormErrorsContext<Props>(
  function CreateUserForm({
    showTeams,
    isPopover,
    captureFormErrorsFromRejection,
    onConfirm,
    onCreateUser,
    hideGuestInvite,
    ...other
  }: Props) {
    const {is_saml} = useAccountSettingsState().data!;
    const {is_super_manager} = useCurrentUserState().data!;

    const [createUser] = useCreateUser();
    const [inviteUser] = useInviteUser();

    const [isCreating, setIsCreating] = useState(false);
    const [allowEdit, setAllowEdits] = useState(false);
    const [selectedTeams, selectTeams] = useState<Array<TeamSerializedType>>(
      []
    );
    const [is_guest_until, set_is_guest_until] = useState<string | undefined>(
      undefined
    );

    const isGuest = is_guest_until != null;

    const [inputs, updateInputs] = useState({
      email: '',
      restrictSso: is_saml,
    });

    const onError = (err: Error) => {
      captureFormErrorsFromRejection(err, true);
      setIsCreating(false);
      return err;
    };

    const inviteToTeams = async (
      user: UserSerializedType
    ): Promise<UserSerializedType> => {
      if (selectedTeams.length === 0) {
        return Promise.resolve(user);
      }

      const invitations = selectedTeams.map<NewInvitationType>((team) => ({
        send_mail: false,
        content_type: 'team',
        permission: allowEdit ? 'edit' : 'view',
        object_id: team.id,
        to_user_id: user.url,
      }));

      await inviteUser({invitations});

      return user;
    };

    const localCreateUser = (name: string): Promise<unknown> => {
      setIsCreating(true);

      const createUserEventChain = isGuest
        ? createUser({
            name,
            email: inputs.email,
            is_guest_until,
          }).unwrap()
        : createUser({
            name,
            email: inputs.email,
            restrict_saml_only_login: inputs.restrictSso,
          })
            .unwrap()
            .then(inviteToTeams);

      return createUserEventChain
        .then((user) => {
          if (onConfirm != null) {
            onConfirm();
          }
          return onCreateUser(user);
        })
        .catch(onError);
    };

    const handleInputChange = (
      event: React.SyntheticEvent<HTMLInputElement>
    ) => {
      const target = event.currentTarget;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      const name = target.name;

      updateInputs({
        ...inputs,
        [name]: value,
      });
    };

    if (isPopover) {
      refreshPopoversPositions();
    }

    return (
      <GeneralCreateItem
        {...other}
        confirmDisabled={
          showTeams && !is_super_manager && selectedTeams.length === 0
        }
        isPopover={isPopover}
        isCreating={isCreating}
        namePlaceholder={!isGuest ? gt("Member's name") : gt("Guest's name")}
        createLabel={!isGuest ? gt('Add member') : gt('Add Guest')}
        onCreate={localCreateUser}
      >
        <FormControlGroup field="email">
          <input
            className={`form-control ${isPopover ? 'form-control-sm' : ''}`}
            type="email"
            maxLength={64}
            name="email"
            placeholder={!isGuest ? gt("Member's email") : gt("Guest's email")}
            onChange={handleInputChange}
            value={inputs.email}
          />
        </FormControlGroup>

        {!hideGuestInvite ? (
          <GuestUserToggleField
            is_guest_until={is_guest_until}
            onChange={set_is_guest_until}
          />
        ) : null}

        {is_saml && !isGuest && (
          <FormControlGroup field="restrictSso">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="restrictSso"
                onChange={handleInputChange}
                checked={inputs.restrictSso}
                id="restrictSsoId"
              />
              <label
                className="form-check-label"
                htmlFor="restrictSsoId"
                title={gt('New member will only be able to login via SSO')}
              >
                {gt('Restrict SSO login')}
              </label>
            </div>
          </FormControlGroup>
        )}

        {showTeams && !isGuest ? (
          <div className="my-2">
            <CollapsableTeamPickerForm
              disabledText={(team) =>
                team.can_edit
                  ? undefined
                  : gt('Only managers can invite to team')
              }
              selected={selectedTeams}
              onChange={selectTeams}
            >
              {selectedTeams.length > 0 ? (
                <div className="small">
                  <div className="form-check my-2">
                    <CheckboxButton
                      id="give-manager-access-checkbox"
                      className="form-check-input"
                      onClick={() => setAllowEdits(!allowEdit)}
                      isChecked={allowEdit}
                      useRealCheckbox={true}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="give-manager-access-checkbox"
                    >
                      {gt('Give manager access to selected teams')}
                    </label>
                  </div>
                </div>
              ) : null}
            </CollapsableTeamPickerForm>
          </div>
        ) : null}
      </GeneralCreateItem>
    );
  }
);
